import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Avatar, makeStyles, Tooltip } from '@material-ui/core';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { useTranslations } from 'examsbook-react-sdk/custom-hooks';
import NotificationBar from '../components/common/Header/NotificationBar';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { CircularProgressbar } from 'react-circular-progressbar';
import { ReactComponent as GreenTick } from '../assets/images/greenTick.svg';
import moment from 'moment';
import { getCourseGroupById } from 'examsbook-react-sdk/services/courseService';
import { MyCoursesCard } from '../components/MyCourseCard/MyCoursesCard';
import { useDispatch, useSelector } from 'react-redux';
import { certificateListAction } from 'examsbook-react-sdk/actions/certificateAddResponse';

import '../components/common/Header/Header.scss';
import '../components/Tabs/SelfLearning.scss';
import '../components/CoursesList/coursesList.scss';

const useStyles = makeStyles(() => ({
    page: {
        backgroundColor: '#f5f5f5',
        minHeight: 'calc(100vh - 60px)',
        padding: '24px',
        overflowX: 'hidden',
        '@media (max-width: 959px)': { padding: '80px 16px 64px' },
        '@media (max-width: 599px)': { padding: '80px 16px 16px' },
    },
    layout: {
        display: 'flex',
        gap: '20px',
        alignItems: 'stretch',
        '@media (max-width: 959px)': { flexDirection: 'column' },
    },
    left: { flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' },
    right: {
        width: '300px',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        '@media (max-width: 959px)': { width: '100%' },
    },
    // Info card — matches InternalCourse root
    infoCard: {
        width: '100%',
        padding: 20,
        borderRadius: '8px',
        background: '#FFFFFF',
        border: '1px solid #75768029',
        boxSizing: 'border-box',
    },
    infoTopRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '12px',
    },
    // matches InternalCourse title
    packageName: {
      color: '#1A1B1E',
      fontWeight: '600',
      fontSize: '20px',
      '@media (max-width: 767px)': {
        fontSize: 14
      },
    },
    // matches InternalCourse description
    description: {
      color: '#000000',
      fontWeight: '400',
      fontSize: '14px',
      margin: 0,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    completedDateText: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        color: '#2E7D32',
        fontWeight: '600',
        fontSize: '14px',
    },
    expiredText: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        color: '#d32f2f',
        fontWeight: '600',
        fontSize: '14px',
    },
    statusRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '8px',
        marginTop: '12px',
    },
    // matches completedPercentageText from SelfLearningCard
    completedText: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        color: '#2E7D32',
        fontWeight: '600',
        fontSize: '14px',
    },
    // Courses section card
    coursesCard: {
        background: '#FFFFFF',
        borderRadius: '8px',
        border: '1px solid #75768029',
        padding: '20px',
        marginTop: '16px',
        marginBottom: '8px',
    },
    courseGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: '16px',
        marginTop: '16px',
        '@media (max-width: 599px)': { gridTemplateColumns: '1fr' },
    },
    courseProgressText: {
        color: '#000000B2',
        fontWeight: 500,
        fontSize: '14px',
    },
    // Right panel — matches InternalCourse root exactly
    creatorCard: {
        width: '100%',
        padding: 20,
        borderRadius: '8px',
        background: '#FFFFFF',
        border: '1px solid #75768029',
        boxSizing: 'border-box',
        flex: 1,
    },
    // matches InternalCourse title row
    cardTitle: {
        color: '#000000',
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: '21px',
        display: 'flex',
        justifyContent: 'space-between',
        textTransform: 'capitalize',
        marginBottom: '4px',
    },
    // matches courseCreatorsBox
    creatorRow: {
        display: 'flex',
        gap: '12px',
        padding: '10px',
        alignItems: 'center',
    },
    // matches courseCreatorsName
    creatorName: {
        color: '#202020',
        fontWeight: 400,
        fontSize: '16px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '30ch',
        '@media (max-width: 767px)': { fontSize: 14 },
    },
    userImage: {
        width: '45px',
        height: '45px',
        borderRadius: '50%',
    },
}));

function CircularProgress({ value, size = 22 }) {
    return (
        <Box style={{ width: size, height: size }}>
            <CircularProgressbar
                value={value}
                styles={{ path: { stroke: '#555', strokeWidth: '10px' }, trail: { stroke: '#e0e0e0' } }}
            />
        </Box>
    );
}

CircularProgress.propTypes = {
    value: PropTypes.number,
    size: PropTypes.number,
};


function CoursePackageDetail() {
    const location = useLocation();
    const history = useHistory();
    const t = useTranslations();
    const classes = useStyles();
    const dispatch = useDispatch();
    
    const { id } = useParams();
    const [pkg, setPkg] = useState(location?.state?.packageData || null);

    const [isLoading, setIsLoading] = useState(false);
    const settingsData = useSelector((state) => state.settingsReducers.settingsData?.data);
    const [contentData, setContentData] = useState([]);
    
    
    const [certificateListing, setCertificateListing] = useState([]);

    useEffect(() => {
        const fetchCourseGroup = async () => {
            if (!id) {
                return;
            }

            try {
                setIsLoading(true);
                const res = await getCourseGroupById(id);

                // API response shape: { groupData: {...}, data: [{ courses: [...] , progressPercent: ... }] }
                const fetchedGroup = res?.groupData || null;
                const userGroupData = Array.isArray(res?.data) ? res.data[0] : null;

                if (fetchedGroup) {
                    setPkg({
                        ...fetchedGroup,
                        progressPercent: userGroupData?.progressPercent ?? fetchedGroup?.progressPercent ?? 0,
                        assignedCourseEndDate: userGroupData?.assignedCourseEndDate || fetchedGroup?.assignedCourseEndDate || null,
                        courses: userGroupData?.courses || fetchedGroup?.courses || [],
                    });
                }
            } catch (error) {
                console.error('Failed to fetch course package detail:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCourseGroup();
    }, [id]);

    useEffect(() => {
        const courses = Array.isArray(pkg?.courses) ? pkg.courses : [];

        const normalizedCourses = courses
            .map((course) => {
                // Handle both nested courseId structure (enriched API) and flat structure (current API)
                const courseObj = course?.courseId || course;
                
                return {
                    // Preserve all original fields
                    ...course,
                    // Ensure courseId is properly structured
                    courseId: courseObj ? {
                        _id: courseObj?._id,
                        name: courseObj?.name,
                        description: courseObj?.description || '',
                        image: courseObj?.image,
                        featured: courseObj?.featured,
                        isSelfLearning: courseObj?.isSelfLearning || false,
                        isLive: courseObj?.isLive || false,
                    } : course,
                    // Ensure featured image is set (with fallbacks)
                    featured: course?.courseId?.featured || course?.featured || course?.courseId?.image || course?.image || '',
                    // Keep card badges in sync for both API shapes
                    isSelfLearning: course?.isSelfLearning ?? courseObj?.isSelfLearning ?? false,
                    isLive: course?.isLive ?? courseObj?.isLive ?? false,
                    // Set progress defaults if enriched data available, otherwise default to 0
                    progressPercent: course?.progressPercent === undefined ? 0 : course?.progressPercent,
                    // MyCoursesCard reads endDate for display, so keep both fields aligned
                    endDate: course?.endDate || course?.assignedCourseEndDate || pkg?.assignedCourseEndDate || null,
                    assignedCourseEndDate: course?.assignedCourseEndDate || course?.endDate || pkg?.assignedCourseEndDate || null,
                    completedOnDate: course?.completedOnDate || null,
                    // Set expiry behavior based on whether we have user assignment data
                    neverExpired: course?.neverExpired ?? false, // Only set neverExpired if using flat API response
                    // Gamification points with defaults
                    userAwardedPoint: course?.userAwardedPoint ?? 0,
                    courseTotalPoint: course?.courseTotalPoint ?? 0,
                };
            })
            .sort((a, b) => new Date(b.startDate || b.assignedCourseEndDate || 0) - new Date(a.startDate || a.assignedCourseEndDate || 0));

        setContentData(normalizedCourses);
    }, [pkg]);

    useEffect(() => {
        const fetchCertificateListing = async () => {
            try {
                const res = await dispatch(certificateListAction());
                if (Array.isArray(res?.data)) {
                    setCertificateListing(res.data);
                }
            } catch (error) {
                console.error('Failed to fetch certificate list:', error);
            }
        };

        fetchCertificateListing();
    }, []);

    const averageProgressPercent = contentData.length > 0
        ? Math.round(
            contentData.reduce((sum, course) => sum + (Number(course?.progressPercent) || 0), 0) / contentData.length,
        )
        : 0;

    const isCompleted = averageProgressPercent >= 100;
    const endDate = pkg?.assignedCourseEndDate ? moment(pkg.assignedCourseEndDate).format('DD-MMM-YYYY') : null;
    const isExpired = pkg?.assignedCourseEndDate && new Date(pkg.assignedCourseEndDate) < new Date() && !isCompleted;

    return (
        <Box>
            {/* Header - desktop only */}
            <Box className="container sticky-header package-detail-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '16px' }}>
                    <ArrowBackIcon style={{ cursor: 'pointer' }} onClick={() => history.push('/course-package')} />
                    <Typography className="greeting-text">{t('Course Package')}</Typography>
                </Box>
                <Box className="notification-bar">
                    <NotificationBar />
                </Box>
            </Box>

            {/* Body */}
            <Box className={classes.page}>
                <Box className={classes.layout}>
                    {isLoading && !pkg ? (
                        <Box className={classes.infoCard}>
                            <Typography variant="body2">{t('Loading')}...</Typography>
                        </Box>
                    ) : (
                        <>
                            {/* LEFT */}
                            <Box className={classes.left}>
                                {/* Info card */}
                                <Box className={classes.infoCard}>
                                    <Box className={classes.infoTopRow}>
                                        <Typography variant="h4" className={classes.packageName}>{pkg?.name}</Typography>
                                        {isCompleted && endDate && (
                                            <Box className={classes.completedDateText}>
                                                <CheckCircleIcon style={{ fontSize: '16px' }} />
                                                {t('Completed on')} {endDate}
                                            </Box>
                                        )}
                                        {isExpired && endDate && (
                                            <Box className={classes.expiredText}>
                                                <CheckCircleIcon style={{ fontSize: '16px' }} />
                                                {t('Expired on')} {endDate}
                                            </Box>
                                        )}
                                        {!isCompleted && !isExpired && endDate && (
                                            <Box className={classes.expiredText} style={{ color: '#555' }}>
                                                {t('Expiring on')} {endDate}
                                            </Box>
                                        )}
                                    </Box>

                                    <Typography variant="h6" className={classes.description}>{pkg?.description}</Typography>
                                    <Box className={classes.statusRow}>
                                        {isCompleted ? (
                                            <Box className={classes.completedText}>
                                                <GreenTick style={{ width: '18px', height: '18px' }} />
                                                {averageProgressPercent}% {t('Completed')}
                                            </Box>
                                        ) : (
                                            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <CircularProgress value={averageProgressPercent} size={22} />
                                                <Typography className={classes.courseProgressText}>
                                                    {averageProgressPercent}% {t('Completed')}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            {/* RIGHT */}
                            <Box className={classes.right}>
                                <Box className={classes.creatorCard}>
                                    <Box className={classes.cardTitle}>
                                        <Typography variant="inherit">
                                            {t('Course Package Creator')}
                                        </Typography>
                                    </Box>
                                    <Box style={{ overflowY: 'auto' }}>
                                        <Box className={classes.creatorRow}>
                                            <Avatar
                                                className={classes.userImage}
                                                src={pkg?.creatorImage}
                                                style={{ backgroundColor: pkg?.creatorImage ? undefined : '#0c3450' }}
                                            />
                                            <Box style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                <Tooltip title={pkg?.creator || ''} placement="top-start">
                                                    <Box className={classes.creatorName}>{pkg?.created?.by?.name || 'Unknown'}</Box>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>

                {/* Courses tab card */}
                <Box className={classes.coursesCard}>
                    <Box style={{ borderBottom: '1px solid #e0e0e0', marginBottom: '0' }}>
                        <Typography style={{ display: 'inline-block', fontSize: '14px', fontWeight: 500, color: '#000000DE', borderBottom: '2px solid #0c3450', paddingBottom: '10px' }}>
                            {t('Courses')}
                        </Typography>
                    </Box>

                    {/* Course cards grid */}
                    {contentData?.length > 0 ? (
                        <Box className={classes.courseGrid}>
                            {contentData.map((course) => (
                                <MyCoursesCard
                                    key={course._id || course.courseId?._id}
                                    testData={course}
                                    settingsData={settingsData}
                                    certificateListing={certificateListing}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography className={classes.description} style={{ padding: '16px 0' }}>
                            {t('No courses in this package')}
                        </Typography>
                    )}
                </Box>
            </Box>

            
        </Box>
    );
}

export default CoursePackageDetail;

